/* Physical layout for the local product proof. All returned geometry is mm.
 * BloomBalance remains the owner of glyph geometry and independent overrides.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./je-birthflower-balance.solver.js'));
  else root.BloomProductSizing = factory(root.BloomBalance);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (balance) {
  'use strict';
  if (!balance) throw new Error('Load balance.solver.js before product-sizing.js.');
  const DEFAULTS = Object.freeze({
    widthMm: 60, heightMm: 170, maxInkWidthMm: 60,
    nominalFontSizeMm: 36.7, nominalFlowerWidthMm: 40,
    minimumFlowerWidthMm: 32, shortFlowerWidthMm: 40,
    targetHeightMm: 145, typicalMinHeightMm: 140, typicalMaxHeightMm: 150,
    typicalMinWidthMm: 36, typicalMaxWidthMm: 40,
    longNameReferenceFontSizeMm: 25.5, longNameReferenceFlowerWidthMm: 45,
    matchNames: true
  });
  const EPSILON = 1e-7;
  function profileForSize(size = '24oz') {
    const normalized = String(size).toLowerCase().replace(/\s/g, '');
    if (!['24oz', '32oz', '40oz'].includes(normalized)) throw new RangeError('Choose a supported Owala size.');
    const scale = normalized === '40oz' ? 210 / 203.2 : 1;
    const profile = { ...DEFAULTS, size: normalized, faceHeightMm: normalized === '40oz' ? 210 : 203.2 };
    // Increase the composition, not the customer's chosen multipliers. The
    // maximum width stays 60 mm; the 180 mm allowance is still exceptional.
    for (const key of ['nominalFontSizeMm', 'nominalFlowerWidthMm', 'minimumFlowerWidthMm',
      'shortFlowerWidthMm', 'targetHeightMm', 'typicalMinHeightMm', 'typicalMaxHeightMm',
      'typicalMinWidthMm', 'typicalMaxWidthMm', 'longNameReferenceFontSizeMm', 'longNameReferenceFlowerWidthMm']) {
      profile[key] *= scale;
    }
    profile.heightMm = normalized === '40oz' ? 180 : 170;
    return Object.freeze(profile);
  }
  function fit(bounds, width, height) {
    return bounds.width <= width + EPSILON && bounds.height <= height + EPSILON;
  }
  function maximum(low, high, predicate) {
    if (predicate(high)) return high;
    for (let i = 0; i < 52; i++) {
      const middle = (low + high) / 2;
      if (predicate(middle)) low = middle;
      else high = middle;
    }
    return low;
  }
  function solveProduct(rows, settings = {}) {
    const profile = profileForSize(settings.size);
    const o = { ...profile, ...settings };
    const numeric = Object.keys(DEFAULTS).filter(key => key !== 'matchNames');
    if (!numeric.every(key => Number.isFinite(o[key]) && o[key] > 0) ||
        o.widthMm > 60 || o.maxInkWidthMm > 60 || o.heightMm > profile.heightMm ||
        o.maxInkWidthMm > o.widthMm || o.typicalMinHeightMm > o.targetHeightMm ||
        o.targetHeightMm > o.typicalMaxHeightMm || o.typicalMaxHeightMm > o.heightMm ||
        o.minimumFlowerWidthMm > o.typicalMinWidthMm ||
        o.typicalMinWidthMm > o.nominalFlowerWidthMm ||
        o.nominalFlowerWidthMm > o.typicalMaxWidthMm ||
        o.typicalMaxWidthMm > o.shortFlowerWidthMm || o.shortFlowerWidthMm > o.maxInkWidthMm ||
        o.longNameReferenceFlowerWidthMm > o.maxInkWidthMm) {
      throw new RangeError('Invalid physical canvas or design targets.');
    }
    if (!Array.isArray(rows)) throw new TypeError('Provide rendered metric rows.');
    const entries = rows.map(row => {
      const flower = { ...balance.DEFAULTS.flower, ...row.flower };
      const alphaWidth = flower.xMax - flower.xMin;
      const alphaHeight = flower.yMax - flower.yMin;
      const numbers = [row.unitsPerEm, row.advance, row.ink?.x, row.ink?.y, row.ink?.width, row.ink?.height,
        row.stem?.advance, row.stem?.endpoint?.x, row.stem?.endpoint?.y,
        flower.anchorX, flower.anchorY, flower.xMin, flower.xMax, flower.yMin, flower.yMax];
      if (!numbers.every(Number.isFinite) || row.unitsPerEm <= 0 || row.ink.width <= 0 ||
          row.ink.height <= 0 || alphaWidth <= 0 || alphaHeight <= 0) {
        throw new TypeError('Finite rendered ink bounds, stem metrics and flower alpha geometry are required.');
      }
      // Put the anchor slightly inside the flower stem instead of aligning two
      // antialiased endpoints edge-to-edge. The overlap scales with the flower
      // (0.2 mm for a 40 mm flower), and remains part of the solved geometry so
      // the on-bottle proof and actual-size production export stay identical.
      flower.anchorY -= Math.min(alphaWidth * .005, alphaHeight * .01);
      const measure = (fontMm, imageMm) => balance.geometry(row, fontMm, imageMm, flower).bounds;
      // Determine natural long-name eligibility in a neutral reference layout.
      // The new, more prominent name style must not itself promote ordinary
      // compositions into the exceptional 170 mm allowance.
      const referenceBounds = measure(o.longNameReferenceFontSizeMm,
        o.longNameReferenceFlowerWidthMm / alphaWidth);
      const needsExtraHeight = referenceBounds.height > o.typicalMaxHeightMm + EPSILON;
      const minimumImage = (needsExtraHeight ? o.minimumFlowerWidthMm : o.typicalMinWidthMm) / alphaWidth;
      const availableHeight = needsExtraHeight
        ? Math.min(o.heightMm, referenceBounds.height) : o.typicalMaxHeightMm;
      // A genuinely exceptional name may need a smaller baseline after its
      // flower reaches the physical floor. Size overrides are ignored here.
      const fontLimit = maximum(0, o.nominalFontSizeMm,
        fontMm => fit(measure(fontMm, minimumImage), o.maxInkWidthMm, availableHeight));
      if (!(fontLimit > 1e-8) || !fit(measure(fontLimit, minimumImage), o.maxInkWidthMm, availableHeight)) {
        throw new RangeError('This flower geometry cannot fit the physical limits at its minimum width.');
      }
      return { row, flower, alphaWidth, minimumImage, measure, fontLimit, needsExtraHeight, availableHeight };
    });
    if (!entries.length) return [];
    const commonFont = Math.min(...entries.map(entry => entry.fontLimit));
    return entries.map(({ row, flower, alphaWidth, minimumImage, measure, fontLimit, needsExtraHeight, availableHeight }, index) => {
      const baseFont = o.matchNames ? commonFont : fontLimit;
      const nominalImage = o.nominalFlowerWidthMm / alphaWidth;
      const nominalBounds = measure(baseFont, nominalImage);
      let desiredImage = nominalImage;
      let heightTarget = o.typicalMaxHeightMm;
      let classification = 'standard';
      if (nominalBounds.height > o.typicalMaxHeightMm) {
        const standardMin = o.typicalMinWidthMm / alphaWidth;
        if (fit(measure(baseFont, standardMin), o.maxInkWidthMm, o.typicalMaxHeightMm)) {
          // Reduce a 40 mm flower only as far as 36 mm to keep an ordinary
          // composition within 150 mm while retaining the lettering weight.
          desiredImage = nominalImage;
        } else if (needsExtraHeight) {
          // A naturally long composition may need extra height. Start with
          // the compact flower; use the physical floor only if 170 mm requires it.
          desiredImage = standardMin;
          heightTarget = availableHeight;
          classification = 'long-skinny';
        }
      }
      // Short names retain the 40 mm flower rather than inflating to fill height.
      // Minimum floor may itself require >150 mm; that is the long-name case.
      if (needsExtraHeight && !fit(measure(baseFont, minimumImage), o.maxInkWidthMm, heightTarget)) {
        heightTarget = availableHeight; classification = 'long-skinny';
      }
      const baseFlower = maximum(minimumImage, Math.max(minimumImage, desiredImage),
        imageMm => fit(measure(baseFont, imageMm), o.maxInkWidthMm, heightTarget));
      const baseline = measure(baseFont, baseFlower);
      // Grant only height already needed by the automatic composition. Slider
      // requests never participate in this decision, so enlarging ordinary
      // names or flowers cannot unlock the exceptional 170 mm allowance.
      const permittedHeight = Math.min(o.heightMm, Math.max(o.typicalMaxHeightMm, baseline.height));
      const extendedHeight = permittedHeight > o.typicalMaxHeightMm + EPSILON;
      if (baseline.height > o.typicalMaxHeightMm + EPSILON) classification = 'long-skinny';
      else if (baseline.width > o.typicalMaxWidthMm + EPSILON) classification = 'short-wide';
      else classification = 'standard';
      // Reuse the existing independent-control solver, supplying this exact
      // physical baseline. A one-row call prevents overrides touching neighbors.
      const result = balance.solveBalance([{ ...row, id: row.id ?? String(index), flower }], {
        width: o.maxInkWidthMm, height: permittedHeight, padding: 0,
        maxFontSize: baseFont, flowerRatio: baseFlower / baseFont,
        minimumFlowerFactor: 1, matchNames: true
      })[0];
      const offsetX = (o.widthMm - o.maxInkWidthMm) / 2;
      const offsetY = (o.heightMm - permittedHeight) / 2;
      result.join.x += offsetX; result.textTransform.x += offsetX; result.flowerPosition.x += offsetX;
      result.bounds.xMin += offsetX; result.bounds.xMax += offsetX;
      result.join.y += offsetY; result.textTransform.y += offsetY; result.flowerPosition.y += offsetY;
      result.bounds.yMin += offsetY; result.bounds.yMax += offsetY;
      return {
        ...result, units: 'mm', board: { width: o.widthMm, height: o.heightMm, padding: 0 },
        widthMm: result.bounds.width, heightMm: result.bounds.height,
        baselineWidthMm: baseline.width, baselineHeightMm: baseline.height,
        classification, extendedHeight, nominalFontSizeMm: o.nominalFontSizeMm,
        baselineReduced: baseFont < o.nominalFontSizeMm - EPSILON,
        sharedBaselineReduced: Boolean(o.matchNames && baseFont < fontLimit - EPSILON),
        visibleFlowerWidthMm: result.flowerSize * alphaWidth,
        baselineFlowerWidthMm: baseFlower * alphaWidth,
        baseFlowerFactor: baseFlower / nominalImage,
        flowerFactor: result.flowerSize / nominalImage,
        nameFactor: result.fontSize / o.nominalFontSizeMm,
        target: { widthMm: o.nominalFlowerWidthMm, heightMm: o.targetHeightMm,
          typicalMinWidthMm: o.typicalMinWidthMm, typicalMaxWidthMm: o.typicalMaxWidthMm,
          typicalMinHeightMm: o.typicalMinHeightMm, typicalMaxHeightMm: o.typicalMaxHeightMm },
        limitsMm: { width: o.maxInkWidthMm, height: permittedHeight },
        fits: result.fits && result.bounds.width <= o.maxInkWidthMm + EPSILON &&
          result.bounds.height <= permittedHeight + EPSILON
      };
    });
  }
  return Object.freeze({ DEFAULTS, profileForSize, solveProduct });
});
